import React, { useRef, useState, useEffect } from "react";
import ReactToPrint from "react-to-print";
import {connect} from "react-redux";
// L'édition d'adresse au niveau Order n'est plus utilisée (édition au niveau Shipment uniquement)
import { CardGiftcard } from "@mui/icons-material";
import { withStyles} from "@mui/styles";
import { FormattedDate, FormattedTime, FormattedMessage } from "react-intl";
import { Button } from "@mui/material"

import showStyle from "assets/jss/views/picking/order/showStyle";
import GridContainer from "components/grid/gridContainer";
import GridItem from "components/grid/gridItem";
import Card from "components/card/card";
import CardHeader from "components/card/cardHeader";
import CardBody from "components/card/cardBody";
import ReceiptCard from "views/picking/order/components/ReceiptCard";
import Table from "views/picking/order/showTable";
import Justification from "views/picking/order/justification"
import Runner from "views/picking/order/components/runner"
import OrderStatus from "views/picking/order/components/orderStatus"
import {
    getShippedStatus,
    LineItemStatus
} from "utils/LineItemStatus";

import {capitalize} from "utils/capitalize";
import {Authorizations, hasAuthorization} from "utils/authorizations";

function TableCard({ classes, retrieved, authorizations, currentOrganization, order, state, openCancellationDialogByTransitionAndItem, toggleModalWaybillSelector, refreshOrder, match, setSelectedShipment, setLineItemSelectedIds, shouldResetSelection, isWmsEnabled }) {
    const orderStatusBeforeCompleted = [
        LineItemStatus.PENDING,
        LineItemStatus.RESERVED,
        LineItemStatus.TO_SHIP,
    ].includes(order?.status) ?? false;

    const justifRef = useRef(null);

    const receiptsMap = (() => {
        const map = {};
        for (const r of (order?.receipts ?? [])) {
            if (r.retailerId) {
                map[`/retailers/${r.retailerId}`] = r;
            }
        }
        return map;
    })();

    const [toShipItems, setToShipItems] = useState([]);
    const [shippedItems, setShippedItems] = useState([]);
    const [hasGiftWrapping, setHasGiftWrapping] = useState(null);
    const [runnerExists, setRunnerExists] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);

    /**
     * Preparing all needed data when retrieved data is updated
     */
    useEffect(() => {
        if (retrieved === null) {
            return;
        }

        const lineItems = retrieved["hydra:member"];

        setToShipItems(getNotShippedLineItems(lineItems));
        setShippedItems(getShippedLineItems(lineItems));
        setHasGiftWrapping(lineItems.some(item => item.giftWrapping === true));
        setRunnerExists(checkIfRunnerExist(lineItems));
    }, [retrieved]);

    function getShipmentsByLineItems(lineItems) {
        let shipments = {};

        if (!lineItems) {
            return {};
        }

        // this code is to avoid another api call
        // and a lot of @Groups for apiPlatform
        // we have all the data we want here
        // we just group lineItems in their shipments
        for (let lineItem of lineItems) {
            if (!lineItem.shipments || lineItem.shipments.length === 0) {
                continue;
            }
            for (let shipment of lineItem.shipments) {
                let key = shipment['@id'];

                if (key in shipments) {
                    shipments[key].lineItems.push(lineItem)
                } else {
                    shipments[key] = {
                        shipmentData: shipment,
                        lineItems: [lineItem]
                    }
                }
            }
        }

        return sortShipmentsByCreatedAtDate(shipments)
    }

    /* Obscure code, isn't it ? */
    function sortShipmentsByCreatedAtDate(shipments) {
        // transform object to array
        let sortable = [];
        for (let shipment in shipments) {
            sortable.push([shipment, shipments[shipment]]);
        }

        // sort by date
        sortable.sort(function(a, b) {
            return new Date(a[1].shipmentData.createdAt) - new Date(b[1].shipmentData.createdAt);
        });

        // transform array to object
        let objSorted = {}
        sortable.forEach(function(item){
            objSorted[item[0]] = item[1]
        })

        return objSorted;
    }

    function getShippedLineItems(lineItems) {
        if (!lineItems) {
            return [];
        }

        // Determine which shipments are considered shipped if at least one item in the shipment is shipped
        const shippedStatuses = new Set(getShippedStatus());
        const shippedShipmentIds = new Set();

        for (const li of lineItems) {
            if (!li.shipments || li.shipments.length === 0) continue;
            if (shippedStatuses.has(li.status)) {
                for (const sh of li.shipments) {
                    shippedShipmentIds.add(sh['@id']);
                }
            }
        }

        // Include every item that belongs to a shipped shipment (even if canceled/unavailable)
        return lineItems.filter(li => {
            if (!li.shipments || li.shipments.length === 0) return false;
            return li.shipments.some(sh => shippedShipmentIds.has(sh['@id']));
        });
    }

    function getNotShippedLineItems(lineItems) {
        if (!lineItems) {
            return [];
        }

        // Exclude items that belong to any shipment considered shipped
        const shippedStatuses = new Set(getShippedStatus());
        const shippedShipmentIds = new Set();
        for (const li of lineItems) {
            if (!li.shipments || li.shipments.length === 0) continue;
            if (shippedStatuses.has(li.status)) {
                for (const sh of li.shipments) {
                    shippedShipmentIds.add(sh['@id']);
                }
            }
        }

        return lineItems.filter(li => {
            if (!li.shipments || li.shipments.length === 0) return true; // items without shipment remain in to-ship list
            return li.shipments.every(sh => !shippedShipmentIds.has(sh['@id']));
        });
    }

    function checkIfRunnerExist(lineItems) {
        for (let i = 0; i < lineItems.length; i++) {
            if (lineItems[i].runner !== null) {
                return true
            }
        }

        return false;
    };

    function renderRunner(lineItems) {
        if (hasAuthorization(authorizations, Authorizations.PICKING_RETAILER) || hasAuthorization(authorizations, Authorizations.PICKING_READ_ONLY)) {
            return null;
        }

        return (
            <Runner
                runnerExists={runnerExists}
                lineItems={lineItems}
            />
        );
    }

    /**
     * Handle click on a shipment's checkbox
     */
    function handleChangeCheckboxGlobal(shipment) {
        if (shipment === null) {
            setSelectedItems([]);
            return;
        }

        if (!retrieved) {
            return;
        }

        // Get the lineItems for the given shipment only
        const shipmentItems = getItemsForShipment(shipment);
        setSelectedItems(shipmentItems);
    }

    /**
     * This is used to uncheck all items from the parent component
     */
    useEffect(() => {
        if (shouldResetSelection) {
            setSelectedItems([]);
            setSelectedShipment(null);
            setLineItemSelectedIds([]);
            setToShipItems([]);
        }
    }, [shouldResetSelection]);

    /**
     * The logic is performed on a local selectedItems variable
     * So after a change, we update the context's variable
     */
    useEffect(() => {
        // Update context variable
        setLineItemSelectedIds(selectedItems.map(li => li['@id']));

        if (selectedItems.length === 0) {
            setSelectedShipment(null);
            return;
        }

        // Select whole shipment if all items are selected
        const shipment = selectedItems[0].shipments[0];
        const shipmentItems = getItemsForShipment(shipment);
        if (shipmentItems.length === selectedItems.length) {
            setSelectedShipment(shipment);
        } else {
            // Unselect shipment if not all items are selected
            setSelectedShipment(null);
        }

    }, [selectedItems]);

    /**
     * Handle click on a single item's checkbox
     */
    function handleChangeCheckboxItem(item) {
        let newSelected = [...selectedItems];
        if (shouldResetSelection) {
            // Sometimes items can still be left checked even after reset
            newSelected = [];
        }

        const shipment = item.shipments[0];

        // Checking if new item is from another shipment or not
        if (selectedItems.length !== 0) {
            const selectedItemShipment = selectedItems[0].shipments[0];
            if (shipment.id !== selectedItemShipment.id) {
                // Unselect selected items and shipment
                newSelected = [];
                setSelectedShipment(null);
            }
        }

        const currentIndex = selectedItems.indexOf(item)
        if (currentIndex === -1) {
            newSelected.push(item)
        } else {
            newSelected.splice(currentIndex, 1);
        }

        setSelectedItems(newSelected);
    }

    function getItemsForShipment(shipment) {
        return toShipItems.filter((item) => {
            // Only one shipment per item
            const itemShipment = item.shipments[0];
            // Just in case, but should not exist
            if (itemShipment === undefined) {
                return false;
            }
            return itemShipment.id === shipment.id;
        });
    }

    function renderToShipItems() {
        if (toShipItems.length === 0) {
            return null;
        }

        return (
            <Table
                tableData={toShipItems && getNotShippedLineItems(toShipItems)}
                order={order}
                shipments={getShipmentsByLineItems(toShipItems)}
                match={match}
                runnerHeader={runnerExists}
                organizationShippingMethods={state.organizationShippingMethods}
                lineItemSelectedIds={state.lineItemSelectedIds}
                handleChangeCheckboxGlobal={handleChangeCheckboxGlobal}
                handleChangeCheckboxItem={handleChangeCheckboxItem}
                refreshOrder={(waitingTime) => refreshOrder(waitingTime)}
                toggleModalWaybillSelector={toggleModalWaybillSelector}
                setLineItemSelectedIds={setLineItemSelectedIds}
                setSelectedShipment={setSelectedShipment}
                selectedShipment={state.selectedShipment}
                waitingTimeBeforeRefreshingList={state.waitingTimeBeforeRefreshingList}
            />
        );
    }

    function renderShippedItems() {
        if (shippedItems.length === 0) {
            return null;
        }

        return (
            <Table
                order={order}
                shipments={getShipmentsByLineItems(shippedItems)}
                match={match}
                runnerHeader={runnerExists}
                organizationShippingMethods={state.organizationShippingMethods}
                lineItemSelectedIds={state.lineItemSelectedIds}
                handleChangeCheckboxGlobal={handleChangeCheckboxGlobal}
                handleChangeCheckboxItem={handleChangeCheckboxItem}
                refreshOrder={(waitingTime) => refreshOrder(waitingTime)}
                toggleModalWaybillSelector={toggleModalWaybillSelector}
                setLineItemSelectedIds={setLineItemSelectedIds}
                setSelectedShipment={setSelectedShipment}
                selectedShipment={state.selectedShipment}
                waitingTimeBeforeRefreshingList={state.waitingTimeBeforeRefreshingList}
            />
        );
    }

    return (
        <>
            <Card>
                <CardHeader color="info" className={classes.cardHeader}>
                    <GridContainer justifyContent={"space-between"} alignItems={"flex-start"} className={classes.cardOrderHeader}>
                        <GridItem xs={6} className={classes.orderDetails}>
                            {order &&
                                <h3 className={classes.title}>
                                    <div className={classes.orderTitleContent}>
                                        <FormattedMessage id={"picking.order.show.title"}/>&nbsp;
                                        { (currentOrganization?.showDisplayExternalId ?? false) && order.displayExternalId !== order.externalId ?
                                            <b>
                                                #{order.displayExternalId} &nbsp;(#{order.externalId})
                                            </b>
                                            :
                                            <b>#{order.externalId}</b>
                                        }
                                        <div className={classes.orderTitleStatus}>
                                            <span className={order.status === "to-ship" ? classes.toShip : classes[order.status]}>
                                                <FormattedMessage id={"picking.order.show.table.status." + order.status}/>
                                            </span>
                                        </div>
                                        {hasGiftWrapping &&
                                            <div className={classes.giftBadge}>
                                                <CardGiftcard style={{ fontSize: "16px", color: "white" }} />
                                            </div>
                                        }
                                    </div>
                                    { order.sellerId &&
                                        <div>
                                            <FormattedMessage id={"picking.order.show.table.badgeId"}/><b>{order.sellerId}</b>
                                        </div>
                                    }
                                    { order.supplierId &&
                                        <div>
                                            <FormattedMessage id={"picking.order.show.table.supplierId"}/><b>{order.supplierId}</b>
                                        </div>
                                    }
                                    <FormattedDate value={new Date(order.createdAt)} year={"numeric"} month={"numeric"} day={"numeric"}/>
                                    &nbsp;
                                    <FormattedTime value={new Date(order.createdAt)} hour={"numeric"} minute={"numeric"}/>
                                </h3>
                            }
                        </GridItem>
                        <GridItem xs={6} className={classes.customer}>
                            <div>
                                {(order && !hasAuthorization(authorizations, Authorizations.PICKING_HIDE_RGPD_DATA)) && (
                                    <div style={{fontWeight: 600}}>
                                        <FormattedMessage id={"picking.order.show.billingAddress.title"} />
                                    </div>
                                )}
                                <b>
                                    {retrieved &&
                                        capitalize(retrieved["hydra:member"][0].order.customerBillingFirstname || retrieved["hydra:member"][0].order.customerBillingFirstname)
                                            + " " +
                                            capitalize(retrieved["hydra:member"][0].order.customerBillingLastname || retrieved["hydra:member"][0].order.customerBillingLastname)
                                    }
                                </b>
                                {(order && !hasAuthorization(authorizations, Authorizations.PICKING_HIDE_RGPD_DATA)) && (() => {
                                    const line1 = [order.customerBillingCompany, order.customerBillingAddress, order.customerBillingAddressMore]
                                        .filter(Boolean)
                                        .join(', ');
                                    const line2 = [order.customerBillingPostalCode, order.customerBillingCity]
                                        .filter(Boolean)
                                        .join(' ');
                                    const line3 = order.customerBillingCountry;
                                    const phone = order.customerBillingPhone;
                                    const email = order.customerBillingEmail;

                                    return (
                                        <div>
                                            {line1 && (<>{line1}<br /></>)}
                                            {line2 && (<>{line2}<br /></>)}
                                            {line3 && (<>{line3}<br /></>)}
                                            {phone && (<>{phone}<br /></>)}
                                            {email && (<>{email}</>)}
                                        </div>
                                    );
                                })()}
                            </div>
                        </GridItem>
                    </GridContainer>
                    {(() => {
                        const retailersMap = {};
                        for (const lineItem of (retrieved?.["hydra:member"] ?? [])) {
                            const r = lineItem.variation?.retailer;
                            if (r?.['@id'] && !retailersMap[r['@id']]) {
                                retailersMap[r['@id']] = r;
                            }
                        }
                        const retailers = Object.values(retailersMap).sort((a, b) =>
                            a.name.localeCompare(b.name)
                        );
                        if (retailers.length === 0) return <ReceiptCard order={order} initialReceipt={null} />;
                        return retailers.map((retailer) => (
                            <ReceiptCard
                                key={retailer['@id']}
                                order={order}
                                retailer={retailer}
                                initialReceipt={receiptsMap[retailer['@id']] ?? null}
                            />
                        ));
                    })()}
                    <GridContainer justifyContent={"space-between"} className={classes.cardOrderHeader}>
                        <GridItem xs={6} className={classes.orderRunnerDetails}>
                            {(retrieved && !hasAuthorization(authorizations, Authorizations.PICKING_READ_ONLY)) &&
                                <div className={classes.runnerBloc}>
                                    {renderRunner(retrieved["hydra:member"])}
                                    <ReactToPrint
                                        trigger={() => <Button
                                            color="primary"
                                            variant="contained"
                                            classes={{root: classes.justificationButton}}
                                        >
                                            <FormattedMessage id={"picking.order.show.header.justificationPrintBtn"}/>
                                        </Button>}
                                        content={() => justifRef.current}
                                    />
                                    <div className={classes.justificationContainer}>
                                        <Justification ref={justifRef} authorizations={authorizations} />
                                    </div>
                                </div>
                            }
                        </GridItem>
                        <GridItem xs={6} className={classes.orderRunnerDetails}>
                            {retrieved && (
                                <OrderStatus
                                    state={state}
                                    openCancellationDialogByTransitionAndItem={openCancellationDialogByTransitionAndItem}
                                    refreshOrder={refreshOrder}
                                    toggleModalWaybillSelector={toggleModalWaybillSelector}
                                    waitingTimeBeforeRefreshingList={state.waitingTimeBeforeRefreshingList}
                                    isWmsEnabled={isWmsEnabled ?? false}
                                    currentOrganization={currentOrganization}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </CardHeader>
                <CardBody className={classes.cardBody}>
                    {renderToShipItems()}
                </CardBody>
            </Card>
            <Card>
                <CardBody className={classes.cardBody}>
                    {renderShippedItems()}
                </CardBody>
            </Card>
        </>
    );
}

const mapStateToProps = state => {
    return {
        retrieved: state.lineItem.list.retrieved,
        authorizations: state.authentication.authorizations,
        currentOrganization: state.currentOrganization.retrieved ?? null,
    };
};

export default connect(
    mapStateToProps,
)(withStyles(showStyle)(TableCard));
